import { motion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Radar,
  Search,
  ShieldCheck,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CountryTag } from '@/components/extensions/registers/CountryTag'
import { RegisterCatalog } from '@/components/extensions/registers/RegisterCatalog'
import { RegisterSourceCard } from '@/components/extensions/registers/RegisterSourceCard'
import { SnapshotSearchResults } from '@/components/extensions/registers/SnapshotSearchResults'
import { SnapshotStatusCard } from '@/components/extensions/registers/SnapshotStatusCard'
import { PageShell } from '@/components/layout/PageShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SegmentedTabs } from '@/components/ui/segmented-tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  SNAPSHOT_SEARCH_MIN_CHARS,
  useRegistersOverview,
  useSnapshotSearch,
} from '@/features/registers/hooks/use-registers'
import type { RegisterCountry, SearchKind } from '@/services/api/registers'
import { cn } from '@/utils/cn'

const STEP_KEYS = ['1', '2', '3', '4'] as const
/** Hits per kind per page in the search console — small pages, never the whole register. */
const PAGE_SIZE = 8

/** OSINT extension detail — where the registers are read from, tools, snapshot status, and a search console. */
export default function OsintExtensionPage() {
  const { t, i18n } = useTranslation()
  const overview = useRegistersOverview()

  const countries = overview.data?.countries ?? []
  const cz = countries.find(c => c.country === 'cz')
  const sk = countries.find(c => c.country === 'sk')
  const totals = useMemo(() => {
    const subjects = countries.reduce((n, c) => n + c.companies, 0)
    const persons = countries.reduce((n, c) => n + c.persons, 0)
    return { subjects, persons }
  }, [countries])

  const fmt = (n: number) => n.toLocaleString(i18n.language)

  return (
    <PageShell
      hero={
        <Hero
          backTo={{ to: '/extensions', label: t('extensions.backToList') }}
          kicker={t('extensions.osint.kicker')}
          title={t('extensions.osint.title')}
          lede={t('extensions.osint.hero.lede')}
          stats={[
            {
              label: t('extensions.osint.stats.czSubjects'),
              value: cz ? fmt(cz.companies) : '—',
              tag: 'CZ' as const,
            },
            {
              label: t('extensions.osint.stats.skSubjects'),
              value: sk ? fmt(sk.companies) : '—',
              tag: 'SK' as const,
            },
            {
              label: t('extensions.osint.stats.persons'),
              value: countries.length ? fmt(totals.persons) : '—',
            },
            {
              label: t('extensions.osint.stats.registers'),
              value: overview.data ? String(overview.data.catalog.length) : '—',
            },
          ]}
        />
      }
    >
      {/* ── where the registers are read from ────────────────────────────── */}
      {overview.isLoading && <Skeleton className="h-64 rounded-lg" />}
      {/* Guarded on `source` itself: a UI ahead of its API during a rolling deploy
          answers without it, and a missing panel beats a crashed page. */}
      {overview.data?.source && (
        <RegisterSourceCard
          source={overview.data.source}
          collections={countries.map(c => c.collection)}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-12">
        {/* ── narrative column ─────────────────────────────────────────── */}
        <div className="space-y-6 lg:col-span-7">
          <Card className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground">
              {t('extensions.osint.sections.overview.title')}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('extensions.osint.sections.overview.body1')}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('extensions.osint.sections.overview.body2')}
            </p>
          </Card>

          <Card className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground">
              {t('extensions.osint.sections.howItWorks.title')}
            </h2>
            <ol className="mt-3 space-y-3">
              {STEP_KEYS.map((key, i) => (
                <li key={key} className="flex gap-3">
                  <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/5 font-mono text-2xs font-semibold text-primary">
                    {i + 1}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {t(`extensions.osint.steps.${key}.title`)}
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {t(`extensions.osint.steps.${key}.body`)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Card>

          <Card className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground">
              {t('extensions.osint.sections.configuration.title')}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('extensions.osint.sections.configuration.hint')}
            </p>
            <dl className="mt-3 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs md:gap-x-6">
              <dt className="text-muted-foreground">
                {t('extensions.osint.sections.configuration.readFrom')}
              </dt>
              <dd className="font-mono text-foreground max-md:break-all">
                {overview.data?.source?.endpoint ?? '—'}
              </dd>
              <dt className="text-muted-foreground">
                {t('extensions.osint.sections.configuration.collections')}
              </dt>
              <dd className="flex flex-wrap gap-1.5 font-mono text-foreground">
                {countries.map(c => (
                  <Badge key={c.country} variant="outline" className="font-mono">
                    {c.collection}
                  </Badge>
                ))}
              </dd>
              <dt className="text-muted-foreground">
                {t('extensions.osint.sections.configuration.schedule')}
              </dt>
              <dd className="text-foreground">
                {t('extensions.osint.sections.configuration.scheduleValue')}
              </dd>
              <dt className="text-muted-foreground">
                {t('extensions.osint.sections.configuration.tools')}
              </dt>
              <dd className="flex flex-wrap gap-1.5">
                {(overview.data?.tools ?? []).map(tool => (
                  <Badge
                    key={tool.name}
                    variant="muted"
                    className="font-mono text-2xs"
                    title={tool.summary}
                  >
                    {tool.name}
                  </Badge>
                ))}
              </dd>
            </dl>
          </Card>
        </div>

        {/* ── status rail ───────────────────────────────────────────────── */}
        <div className="space-y-4 lg:col-span-5">
          <div className="flex items-baseline justify-between max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-0.5">
            <h2 className="text-sm font-semibold text-foreground">
              {t('extensions.osint.sections.snapshots.title')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t('extensions.osint.sections.snapshots.hint')}
            </p>
          </div>
          {overview.isLoading && (
            <>
              <Skeleton className="h-64 rounded-lg" />
              <Skeleton className="h-64 rounded-lg" />
            </>
          )}
          {overview.isError && (
            <p className="text-xs text-destructive">{(overview.error as Error).message}</p>
          )}
          {countries.map(status => (
            <SnapshotStatusCard
              key={status.country}
              status={status}
              sourceMode={overview.data?.source?.mode}
            />
          ))}
        </div>
      </div>

      {/* ── catalog ──────────────────────────────────────────────────────── */}
      <section>
        <header className="mb-4 flex items-baseline justify-between gap-4 max-md:flex-wrap max-md:gap-y-0.5">
          <h2 className="text-base font-semibold text-foreground">
            {t('extensions.osint.sections.catalog.title')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('extensions.osint.sections.catalog.hint')}
          </p>
        </header>
        {overview.isLoading && (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {['a', 'b', 'c', 'd', 'e', 'f'].map(k => (
              <Skeleton key={k} className="h-36 rounded-lg" />
            ))}
          </div>
        )}
        {overview.data && <RegisterCatalog entries={overview.data.catalog} />}
      </section>

      {/* ── search console ───────────────────────────────────────────────── */}
      <SearchConsole czAvailable={!!cz?.available} skAvailable={!!sk?.available} />
    </PageShell>
  )
}

// ── hero ────────────────────────────────────────────────────────────────────
function Hero({
  kicker,
  title,
  lede,
  stats,
  backTo,
}: {
  kicker: string
  title: string
  lede: string
  stats: { label: string; value: string; tag?: 'CZ' | 'SK' }[]
  /** Where the detail returns to (the extensions list). */
  backTo: { to: string; label: string }
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_80%_at_100%_0%,var(--color-primary)/12%,transparent_60%),radial-gradient(40%_60%_at_0%_100%,var(--color-cat-4)/10%,transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:linear-gradient(var(--color-border)_1px,transparent_1px),linear-gradient(90deg,var(--color-border)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(70%_70%_at_80%_20%,black,transparent)]"
      />
      <div className="relative grid gap-5 p-4 md:gap-6 md:p-6 lg:grid-cols-12 lg:items-end">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 mb-3 w-fit px-2 text-muted-foreground"
          >
            <Link to={backTo.to}>
              <ArrowLeft className="size-4" />
              {backTo.label}
            </Link>
          </Button>
          <div className="flex items-center gap-2 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-primary-strong">
            <Radar className="size-3.5" />
            {kicker}
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-none tracking-tight text-foreground max-md:break-words sm:text-4xl">
            {title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{lede}</p>
        </motion.div>
        <motion.dl
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
          }}
          className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border lg:col-span-5"
        >
          {stats.map(s => (
            <motion.div
              key={s.label}
              variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
              className="bg-card px-4 py-3"
            >
              <dt className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                {s.tag && <CountryTag country={s.tag} />}
                {s.label}
              </dt>
              <dd className="mt-1 font-mono text-xl font-semibold tabular-nums leading-none text-foreground md:text-2xl">
                {s.value}
              </dd>
            </motion.div>
          ))}
        </motion.dl>
      </div>
    </div>
  )
}

// ── search console ──────────────────────────────────────────────────────────
function SearchConsole({
  czAvailable,
  skAvailable,
}: {
  czAvailable: boolean
  skAvailable: boolean
}) {
  const { t } = useTranslation()
  const [country, setCountryState] = useState<RegisterCountry>(
    czAvailable || !skAvailable ? 'cz' : 'sk'
  )
  const [kind, setKindState] = useState<SearchKind>('all')
  const [q, setQState] = useState('')
  const [page, setPage] = useState(1)
  // Any change of query/country/kind starts again from the first page.
  const setCountry = (c: RegisterCountry) => {
    setCountryState(c)
    setPage(1)
  }
  const setKind = (k: SearchKind) => {
    setKindState(k)
    setPage(1)
  }
  const setQ = (value: string) => {
    setQState(value)
    setPage(1)
  }
  const search = useSnapshotSearch(country, q, kind, PAGE_SIZE, page)
  const available = country === 'cz' ? czAvailable : skAvailable
  const ready = q.trim().length >= SNAPSHOT_SEARCH_MIN_CHARS
  const totalFound = search.data
    ? Math.max(search.data.found.companies, search.data.found.persons)
    : 0
  const lastPage = Math.max(1, Math.ceil(totalFound / PAGE_SIZE))

  return (
    <Card className="relative overflow-hidden p-4 md:p-5">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-16 -bottom-16 size-56 rounded-full bg-primary/5 blur-3xl"
      />
      <header className="relative flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-primary-strong">
            <ShieldCheck className="size-3.5" />
            {t('extensions.osint.sections.search.kicker')}
          </div>
          <h2 className="mt-1 text-base font-semibold text-foreground">
            {t('extensions.osint.sections.search.title')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('extensions.osint.sections.search.hint')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedTabs<RegisterCountry>
            size="sm"
            active={country}
            onChange={setCountry}
            tabs={[
              { value: 'cz', label: t('extensions.osint.countries.cz') },
              { value: 'sk', label: t('extensions.osint.countries.sk') },
            ]}
          />
          <SegmentedTabs<SearchKind>
            size="sm"
            active={kind}
            onChange={setKind}
            tabs={[
              { value: 'all', label: t('extensions.osint.sections.search.kind.all') },
              { value: 'company', label: t('extensions.osint.sections.search.kind.company') },
              { value: 'person', label: t('extensions.osint.sections.search.kind.person') },
            ]}
          />
        </div>
      </header>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-5.5 size-4 -translate-y-1/2 text-muted-foreground md:top-1/2" />
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t(`extensions.osint.sections.search.placeholder.${country}`)}
          className={cn('h-11 pl-9 text-base', search.isFetching && 'animate-pulse')}
          autoComplete="off"
        />
        {!available && (
          <p className="mt-2 text-xs text-warning-foreground">
            {t('extensions.osint.sections.search.unavailable')}
          </p>
        )}
      </div>

      <div className="relative mt-4 min-h-10">
        {!ready && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ArrowRight className="size-3.5" />
            {t('extensions.osint.sections.search.typeMore', { n: SNAPSHOT_SEARCH_MIN_CHARS })}
          </p>
        )}
        {search.isError && (
          <p className="text-xs text-destructive">{(search.error as Error).message}</p>
        )}
        {search.data && ready && (
          <SnapshotSearchResults
            data={search.data}
            onOpenCompany={label => {
              setKind('company')
              setQ(label)
            }}
          />
        )}
        {search.data && ready && totalFound > PAGE_SIZE && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {t('extensions.osint.sections.search.pageInfo', {
                page,
                pages: lastPage,
                size: PAGE_SIZE,
              })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1 || search.isFetching}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="size-4" />
                {t('extensions.osint.sections.search.prev')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= lastPage || search.isFetching}
                onClick={() => setPage(p => Math.min(lastPage, p + 1))}
              >
                {t('extensions.osint.sections.search.next')}
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
